// ===================================
// UTILS - Plantilla HTML del comprobante de turno de caja (ticket térmico)
// ===================================
// Solo el resumen del turno (sin movimientos ni firma). Mismo estilo que buildTicketHtml.

import { getScaledFontScale, printPx, type PrintConfig } from '../hooks/usePrintConfig';
import { formatClp, formatFecha, formatHora, formatRut } from './ticket-html';
import { formatMedioPago } from '../pages/caja/utils/formato';

export interface ComprobanteTurnoDatos {
  almacenNombre: string;
  almacenRut: string | null;
  almacenDireccion: string | null;
  vendedor: string;
  estado: string;
  fechaHoraApertura: string;
  fechaHoraCierre: string | null;
  montoInicial: number;
  ventasPorMedioPago: Record<string, number>;
  totalVentas: number | null;
  totalIngresos: number | null;
  totalIngresosEfectivo?: number | null;
  totalEgresos: number | null;
  efectivoEsperado: number | null;
  arqueoRealizado: boolean;
  efectivoContado: number | null;
  diferenciaEfectivo: number | null;
  motivoCierre: string | null;
}

const formatDiferencia = (monto: number): string => (monto < 0 ? `-${formatClp(Math.abs(monto))}` : formatClp(monto));

const escapeHtml = (texto: string): string =>
  texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const buildComprobanteTurnoHtml = (datos: ComprobanteTurnoDatos, printConfig: PrintConfig): string => {
  const s = getScaledFontScale(printConfig);
  const px = (n: number) => printPx(printConfig, n);

  const hr = `<hr style="border:none;border-top:1px dashed #000;margin:${px(4)}px 0"/>`;
  const fila = (etiqueta: string, valor: string, negrita = false) => `
    <tr${negrita ? ` style="font-weight:bold;font-size:${s.md}px"` : ''}>
      <td style="padding:${px(1)}px;text-align:left">${escapeHtml(etiqueta)}</td>
      <td style="padding:${px(1)}px;text-align:right;white-space:nowrap">${valor}</td>
    </tr>`;
  const tabla = (filas: string) =>
    `<table style="width:100%;font-size:${s.sm}px;table-layout:fixed;border-collapse:collapse">${filas}</table>`;

  const ventas = Object.entries(datos.ventasPorMedioPago).filter(([, monto]) => monto > 0);
  const ventasHtml = ventas.length === 0
    ? `<div style="font-size:${s.sm}px;text-align:center">Sin ventas en el turno</div>`
    : tabla(ventas.map(([medio, monto]) => fila(formatMedioPago(medio), formatClp(monto))).join(''));

  const cierreHtml = datos.fechaHoraCierre
    ? `<div>Cierre: ${formatFecha(datos.fechaHoraCierre)} ${formatHora(datos.fechaHoraCierre)}</div>`
    : '';

  const arqueoHtml = datos.fechaHoraCierre && datos.arqueoRealizado
    ? `${hr}${tabla(
        fila('Efectivo contado:', formatClp(datos.efectivoContado ?? 0)) +
          fila('Diferencia:', formatDiferencia(datos.diferenciaEfectivo ?? 0), true)
      )}`
    : '';

  const motivoHtml = datos.motivoCierre
    ? `${hr}<div style="font-size:${s.sm}px"><strong>Motivo de cierre:</strong> ${escapeHtml(datos.motivoCierre)}</div>`
    : '';

  return `
    <div style="text-align:center;margin-bottom:${px(6)}px;font-size:${s.base}px">
      <div style="font-weight:bold">${escapeHtml(datos.almacenNombre)}</div>
      ${datos.almacenRut ? `<div>RUT: ${formatRut(datos.almacenRut)}</div>` : ''}
      ${datos.almacenDireccion ? `<div>${escapeHtml(datos.almacenDireccion)}</div>` : ''}
      <div style="font-weight:bold;font-size:${s.title}px;margin-top:${px(4)}px">COMPROBANTE DE TURNO</div>
    </div>
    ${hr}
    <div style="font-size:${s.sm}px">
      <div>Vendedor: ${escapeHtml(datos.vendedor)}</div>
      <div>Apertura: ${formatFecha(datos.fechaHoraApertura)} ${formatHora(datos.fechaHoraApertura)}</div>
      ${cierreHtml}
      <div>Estado: ${escapeHtml(datos.estado)}</div>
    </div>
    ${hr}
    ${tabla(fila('Monto inicial:', formatClp(datos.montoInicial)))}
    ${hr}
    <div style="font-size:${s.sm}px;font-weight:bold;margin-bottom:${px(2)}px">Ventas por medio de pago</div>
    ${ventasHtml}
    ${hr}
    ${tabla(
      fila('Total ventas:', formatClp(datos.totalVentas ?? 0), true) +
        fila('Ingresos (todos los medios):', formatClp(datos.totalIngresos ?? 0)) +
        (datos.totalIngresosEfectivo != null ? fila('Ingresos en efectivo:', formatClp(datos.totalIngresosEfectivo)) : '') +
        fila('Egresos:', formatClp(datos.totalEgresos ?? 0)) +
        fila('Efectivo esperado:', formatClp(datos.efectivoEsperado ?? 0), true)
    )}
    ${arqueoHtml}
    ${motivoHtml}
    ${hr}
    <div style="text-align:center;margin-top:${px(4)}px;font-size:${s.xs}px;font-style:italic">
      Los movimientos pendientes de aprobación no se incluyen en los totales.
    </div>
    <div style="text-align:center;margin-top:${px(6)}px;font-size:${s.sm}px">Almacenes Digitales</div>`;
};
